import type { Class, Project } from '../../data/types';
import { getPublishTheme } from '../themes';
import type { PublishConfig } from '../types';

/**
 * Pure builders for the static HTML inside a publish bundle.
 *
 * No I/O, no Blobs, no DOM access — these helpers take the kid's
 * config and produce the strings that go into `index.html`. Tests pin
 * the structure + the safety-escaping of project metadata without
 * spinning up a browser.
 *
 * M2 scope: image-kind sticker-cam template. Other templates render
 * as a friendly "coming soon" placeholder so the kid can still
 * download an archive of their config + model files. The runtime
 * inference loop (TF.js + MobileNet + the kid's classifier) is wired
 * for sticker-cam; quiz / reactor get their full runtime in a
 * follow-up.
 */

export interface HtmlTemplateInput {
  project: Pick<Project, 'name' | 'kind'>;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
  config: PublishConfig;
  /** Class labels in the model's output order. The trainer extracts
   *  these alongside the weights. */
  trainedLabels: ReadonlyArray<{ classId: string; name: string }>;
}

/** HTML-escapes user-controlled strings. Defensive even though the
 *  composer caps lengths and the publish endpoint will profanity-
 *  filter; cheap to apply at every interpolation site. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Slugifies a project name into a kid-friendly filename / path stem. */
export function publishFilenameStem(projectName: string): string {
  const trimmed = projectName.trim().toLowerCase();
  const slug = trimmed.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || 'app';
}

export function buildIndexHtml(input: HtmlTemplateInput): string {
  const { config, project } = input;
  const theme = getPublishTheme(config.themeId);
  const title = escapeHtml(config.text.title || project.name);
  const subtitle = config.text.subtitle ? escapeHtml(config.text.subtitle) : '';

  // Inline theme CSS variables so the runtime stylesheet stays single-
  // file and offline-friendly. Hex colours are already validated by
  // the curated theme catalogue.
  const css = renderCss(theme);
  const body = renderBody({ ...input, title, subtitle });
  const runtime = renderRuntime(input);

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
    <meta name="robots" content="noindex" />
    <title>${title}</title>
    <style>${css}</style>
  </head>
  <body data-template="${escapeHtml(config.template)}">
    ${body}
    <script>${runtime}</script>
  </body>
</html>
`;
}

/* ----------------------------- internals ----------------------------- */

function renderCss(theme: ReturnType<typeof getPublishTheme>): string {
  // Self-contained CSS — no dependencies on the studio's tokens. The
  // gradient (when present) layers on top of the solid bg so devices
  // that don't support gradients still get a usable canvas.
  return `
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; min-height: 100%; }
body {
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: ${theme.fg};
  background-color: ${theme.bg};
  ${theme.gradient ? `background: ${theme.gradient};` : ''}
  background-attachment: fixed;
  min-height: 100vh;
}
.bp-stage {
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 1rem;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 1rem;
  min-height: 100vh;
}
.bp-header { display: grid; gap: 0.25rem; }
.bp-title {
  margin: 0;
  font-size: clamp(1.6rem, 4vw, 2.6rem);
  font-weight: 800;
  letter-spacing: -0.01em;
}
.bp-subtitle { margin: 0; opacity: 0.78; font-size: 1rem; }
.bp-camera {
  position: relative;
  background: ${theme.surface};
  border-radius: 16px;
  border: 2px solid color-mix(in srgb, ${theme.fg} 15%, transparent);
  aspect-ratio: 4 / 3;
  overflow: hidden;
  display: grid;
  place-items: center;
}
.bp-camera video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Mirror so the kid moves left → on-screen left. Matches the
     studio's training viewfinder. */
  transform: scaleX(-1);
}
.bp-camera-overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  text-align: center;
  padding: 1rem;
  background: color-mix(in srgb, ${theme.fg} 70%, transparent);
  color: ${theme.bg};
  gap: 0.6rem;
}
.bp-camera-overlay button {
  font: inherit;
  font-weight: 700;
  background: ${theme.accent};
  color: ${theme.accentFg};
  border: none;
  border-radius: 999px;
  padding: 0.65rem 1.4rem;
  cursor: pointer;
}
.bp-burst {
  position: absolute;
  font-size: clamp(2.4rem, 7vw, 4.5rem);
  pointer-events: none;
  transform: translate(-50%, -50%) scale(0.4);
  animation: bp-burst 1100ms cubic-bezier(0.2, 0.7, 0.3, 1.1) forwards;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25));
}
@keyframes bp-burst {
  0% { transform: translate(-50%, -50%) scale(0.4) rotate(-12deg); opacity: 0; }
  20% { opacity: 1; }
  60% { transform: translate(-50%, -50%) scale(1.4) rotate(6deg); opacity: 1; }
  100% { transform: translate(-50%, -120%) scale(1) rotate(0deg); opacity: 0; }
}
.bp-chips {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-wrap: wrap; gap: 0.5rem;
}
.bp-chip {
  display: inline-flex; align-items: center; gap: 0.55rem;
  padding: 0.45rem 0.85rem 0.45rem 0.55rem;
  background: ${theme.surface};
  border: 1px solid color-mix(in srgb, ${theme.fg} 15%, transparent);
  border-radius: 999px;
  font-size: 0.95rem;
  transition: transform 120ms ease;
}
.bp-chip[data-active="true"] {
  transform: scale(1.05);
  border-color: ${theme.accent};
  box-shadow: 0 0 0 2px ${theme.accent};
}
.bp-chip-emoji { font-size: 1.15rem; line-height: 1; }
.bp-chip-name { font-weight: 600; }
.bp-footer {
  text-align: center;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  opacity: 0.55;
}
.bp-status {
  font-size: 0.78rem;
  opacity: 0.62;
  text-align: center;
}
@media (prefers-reduced-motion: reduce) {
  .bp-burst { animation-duration: 0.001ms; opacity: 0; }
  .bp-chip { transition: none; }
}
`;
}

interface RenderBodyArgs extends HtmlTemplateInput {
  title: string;
  subtitle: string;
}

function renderBody(args: RenderBodyArgs): string {
  const { config, classes, title, subtitle } = args;
  const safeTemplate = escapeHtml(config.template);
  const chips = classes
    .map((cls) => {
      const cust = config.perClass[cls.id] ?? {};
      const emoji = escapeHtml(cust.emoji ?? '✨');
      const label = escapeHtml(cust.label ?? cls.name);
      const id = escapeHtml(cls.id);
      return `<li class="bp-chip" data-class-id="${id}">
        <span class="bp-chip-emoji" aria-hidden="true">${emoji}</span>
        <span class="bp-chip-name">${label}</span>
      </li>`;
    })
    .join('');

  // Currently every supported template renders the same body shape
  // (header → camera stage → chip strip → footer). Future templates
  // can branch on `safeTemplate` here without changing the runtime
  // contract.
  const footer = config.branding.showFooter
    ? `<footer class="bp-footer">Made on <strong>Bryta</strong></footer>`
    : '';
  return `
    <main class="bp-stage" data-template="${safeTemplate}">
      <header class="bp-header">
        <h1 class="bp-title">${title}</h1>
        ${subtitle ? `<p class="bp-subtitle">${subtitle}</p>` : ''}
      </header>
      <section class="bp-camera" id="bp-camera" aria-label="Live camera">
        <video id="bp-video" playsinline muted autoplay></video>
        <div class="bp-camera-overlay" id="bp-camera-overlay" role="status">
          <p>Tap to start the camera</p>
          <button type="button" id="bp-start">${escapeHtml(config.text.actionLabel ?? 'Start')}</button>
        </div>
      </section>
      <ul class="bp-chips" aria-label="Class triggers">${chips}</ul>
      <p class="bp-status" id="bp-status" aria-live="polite"></p>
      ${footer}
    </main>
  `;
}

function renderRuntime(input: HtmlTemplateInput): string {
  // The kid's config snapshot lives here as a JSON literal so the
  // runtime can read it without a network round-trip. Per-class
  // mappings are turned into a label→{emoji, soundId} lookup keyed
  // by the trained label NAME (the model emits indices that we map
  // to label names via `trainedLabels`).
  const labelCustomisation: Record<string, { emoji: string }> = {};
  for (const lbl of input.trainedLabels) {
    const c = input.config.perClass[lbl.classId] ?? {};
    labelCustomisation[lbl.name] = { emoji: c.emoji ?? '✨' };
  }
  const runtimeData = {
    template: input.config.template,
    labels: input.trainedLabels.map((l) => l.name),
    perLabel: labelCustomisation,
    minScore: 0.6,
  };
  // Single-class projects can't usefully classify (the model always
  // says the only class). We surface a message and skip inference.
  return `
(function () {
  'use strict';
  var DATA = ${JSON.stringify(runtimeData)};
  var statusEl = document.getElementById('bp-status');
  var overlayEl = document.getElementById('bp-camera-overlay');
  var startBtn = document.getElementById('bp-start');
  var videoEl = document.getElementById('bp-video');
  var cameraEl = document.getElementById('bp-camera');
  var chipsByLabel = {};
  document.querySelectorAll('.bp-chip').forEach(function (el) {
    var name = el.querySelector('.bp-chip-name')?.textContent || '';
    chipsByLabel[name] = el;
  });

  function setStatus(text) {
    if (statusEl) statusEl.textContent = text || '';
  }
  function highlightLabel(label) {
    Object.keys(chipsByLabel).forEach(function (name) {
      chipsByLabel[name].setAttribute('data-active', String(name === label));
    });
  }
  function fireBurst(emoji) {
    if (!cameraEl) return;
    var node = document.createElement('span');
    node.className = 'bp-burst';
    node.textContent = emoji;
    node.style.left = (20 + Math.random() * 60) + '%';
    node.style.top = (20 + Math.random() * 60) + '%';
    cameraEl.appendChild(node);
    setTimeout(function () { node.remove(); }, 1200);
  }

  // Single-class projects: nothing to predict. Skip inference + show
  // the camera with a friendly note.
  if (DATA.labels.length < 2) {
    setStatus('Add more classes in Bryta to see live predictions.');
  }

  function start() {
    if (overlayEl) overlayEl.style.display = 'none';
    setStatus('Loading the model…');
    // Loading TF.js + the kid's classifier is async and chunked.
    // We isolate the bootstrap inside an IIFE so a failure surfaces
    // in the status line rather than freezing the page.
    (async function () {
      try {
        await loadScript('https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.22.0/dist/tf.min.js');
        await loadScript('https://cdn.jsdelivr.net/npm/@tensorflow-models/mobilenet@2.1.1/dist/mobilenet.min.js');
        var stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
        videoEl.srcObject = stream;
        await videoEl.play();
        setStatus('Warming up…');
        var mobilenet = await window.mobilenet.load({ version: 2, alpha: 0.5 });
        var head = await window.tf.loadLayersModel('./model.json');
        var tickInFlight = false;
        var lastFiredLabel = '';
        var lastFiredAt = 0;
        async function tick() {
          if (tickInFlight) return;
          tickInFlight = true;
          try {
            var emb = mobilenet.infer(videoEl, true);
            var logits = head.predict(emb);
            var probs = await logits.data();
            emb.dispose(); logits.dispose();
            var topIdx = 0; var topScore = 0;
            for (var i = 0; i < probs.length; i++) {
              if (probs[i] > topScore) { topScore = probs[i]; topIdx = i; }
            }
            var label = DATA.labels[topIdx];
            highlightLabel(label);
            setStatus(topScore >= DATA.minScore
              ? 'I see ' + label + ' (' + Math.round(topScore * 100) + '%)'
              : 'Hmm — I am not sure');
            // Cool-down so a steady prediction doesn't carpet-bomb
            // the camera with the same emoji 30× per second.
            var now = Date.now();
            if (topScore >= DATA.minScore && (label !== lastFiredLabel || now - lastFiredAt > 600)) {
              var cust = DATA.perLabel[label];
              if (cust && cust.emoji) fireBurst(cust.emoji);
              lastFiredLabel = label;
              lastFiredAt = now;
            }
          } catch (err) {
            console.error('predict tick failed', err);
          } finally {
            tickInFlight = false;
          }
        }
        setStatus('Ready. Show me what I learned!');
        setInterval(tick, 100);
      } catch (err) {
        console.error('Could not start the model:', err);
        setStatus('Could not start: ' + (err && err.message ? err.message : 'unknown error'));
      }
    })();
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = false;
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('Failed to load ' + src)); };
      document.head.appendChild(s);
    });
  }

  if (startBtn) startBtn.addEventListener('click', start);
})();
`;
}
